import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Grid,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Drawer,
    Paper,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    useMediaQuery,
    useTheme,
    Card,
    CardContent,
    Chip
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import AppShortcutIcon from '@mui/icons-material/AppShortcut';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import Cookies from 'js-cookie';
import api from '../../../../services/api';
import NavegadorGestao from '../../../../components/NavBarGestao';
import moment from 'moment';

export default function Ferramentas() {
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);

    // Form state
    const [tipo, setTipo] = useState('APP');
    const [area, setArea] = useState('Ronda Motorizada');
    const [versao, setVersao] = useState('');
    const [file, setFile] = useState(null);

    const userRole = Cookies.get('regra') || '';
    const isRootMaster = userRole.includes('ROOT_MASTER');
    const darkMode = Cookies.get('darkMode') === 'true';

    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

    const [isDrawerOpen, setIsDrawerOpen] = useState(!isMobile);

    useEffect(() => {
        setIsDrawerOpen(!isMobile);
    }, [isMobile]);

    const theme = createTheme({
        palette: {
            mode: darkMode ? 'dark' : 'light',
            primary: { main: '#3b82f6' },
            background: {
                default: darkMode ? '#121212' : '#f8fafc',
                paper: darkMode ? '#1e1e1e' : '#ffffff',
            },
        },
    });

    const fetchApps = async () => {
        try {
            setLoading(true);
            const response = await api.get('v1/protected/ferramentas/listartodos');
            setApps(response.data);
        } catch (error) {
            console.error('Erro ao buscar apps:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchApps();
    }, []);

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!file || !versao) {
            alert('Preencha todos os campos e selecione um arquivo.');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('tipo', tipo);
        formData.append('area', area);
        formData.append('versao', versao);
        formData.append('id_usuario', Cookies.get('id_usuario'));

        try {
            setUploadLoading(true);
            await api.post('v1/protected/ferramentas/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            alert('Upload realizado com sucesso!');
            setIsUploadModalOpen(false);
            setFile(null);
            setVersao('');
            fetchApps();
        } catch (error) {
            console.error('Erro no upload:', error);
            alert('Erro ao realizar upload: ' + (error.response?.data || error.message));
        } finally {
            setUploadLoading(false);
        }
    };

    const columns = useMemo(() => [
        {
            accessorKey: 'tipo',
            header: 'Tipo',
            size: 100,
        },
        {
            accessorKey: 'area',
            header: 'Área',
            size: 150,
        },
        {
            accessorKey: 'versao',
            header: 'Versão',
            size: 100,
        },
        {
            accessorKey: 'dataHoraUpload',
            header: 'Data de Upload',
            Cell: ({ cell }) => moment(cell.getValue()).format('DD/MM/YYYY HH:mm'),
            size: 150,
        },
        {
            accessorKey: 'usuarioUpload.nome_amigavel',
            header: 'Enviado por',
            size: 150,
        },
        {
            accessorKey: 'caminhoArquivo',
            header: 'Ações',
            Cell: ({ cell }) => {
                const physicalPath = cell.getValue();
                const proxyUrl = physicalPath.replace('/home/wwgrup/tools/', 'https://gruporosinetos.com/tools/');
                return (
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<DownloadIcon />}
                        href={proxyUrl}
                        target="_blank"
                    >
                        Download
                    </Button>
                );
            },
            size: 150,
        },
    ], []);

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
                <NavegadorGestao />
                
                <Box sx={{ display: 'flex', flex: 1, flexDirection: isMobile ? 'column' : 'row' }}>
                    {/* Menu Lateral */}
                    <Drawer
                        variant={isMobile ? "temporary" : "permanent"}
                        open={isDrawerOpen}
                        onClose={() => setIsDrawerOpen(false)}
                        sx={{
                            width: 240,
                            flexShrink: 0,
                            [`& .MuiDrawer-paper`]: {
                                width: 240,
                                boxSizing: 'border-box',
                                position: isMobile ? 'fixed' : 'relative',
                                height: isMobile ? '100%' : 'auto',
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                transition: 'width 0.2s',
                                overflowX: 'hidden'
                            },
                        }}
                    >
                        <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end' }}>
                            <IconButton onClick={() => setIsDrawerOpen(false)}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Divider />
                        <List>
                            <ListItem button selected onClick={() => isMobile && setIsDrawerOpen(false)}>
                                <ListItemIcon>
                                    <AppShortcutIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText primary="Apps" />
                            </ListItem>
                        </List>
                    </Drawer>

                    {/* Botão para abrir menu no mobile */}
                    {isMobile && !isDrawerOpen && (
                        <Box sx={{ p: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<MenuIcon />}
                                onClick={() => setIsDrawerOpen(true)}
                                size="small"
                            >
                                Menu
                            </Button>
                        </Box>
                    )}

                    {/* Conteúdo Principal */}
                    <Box component="main" sx={{ flexGrow: 1, p: isMobile ? 1 : 3, overflowX: 'hidden' }}>
                        <Paper sx={{ 
                            p: 2, 
                            mb: 3, 
                            display: 'flex', 
                            flexDirection: isMobile ? 'column' : 'row',
                            justifyContent: 'space-between', 
                            alignItems: isMobile ? 'stretch' : 'center', 
                            borderRadius: 2,
                            gap: 2
                        }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', textAlign: isMobile ? 'center' : 'left' }}>
                                Central de Downloads
                            </Typography>
                            {isRootMaster && (
                                <Button
                                    variant="contained"
                                    startIcon={<CloudUploadIcon />}
                                    onClick={() => setIsUploadModalOpen(true)}
                                    fullWidth={isMobile}
                                >
                                    Fazer Upload
                                </Button>
                            )}
                        </Paper>

                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
                                <CircularProgress />
                            </Box>
                        ) : isMobile ? (
                            /* Versão Mobile em Cards */
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {apps.map((app) => (
                                    <Card key={app.id_ferramenta} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', elevation: 0 }}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                <Chip label={app.tipo} size="small" color="primary" variant="outlined" />
                                                <Typography variant="caption" color="text.secondary">
                                                    v{app.versao}
                                                </Typography>
                                            </Box>
                                            
                                            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem', mb: 1 }}>
                                                {app.area}
                                            </Typography>

                                            <Divider sx={{ my: 1 }} />

                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                                                <Typography variant="body2">
                                                    <b>Enviado:</b> {moment(app.dataHoraUpload).format('DD/MM/YYYY HH:mm')}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <b>Usuário:</b> {app.usuarioUpload?.nome_amigavel || 'Sistema'}
                                                </Typography>
                                            </Box>

                                            <Button
                                                variant="contained"
                                                fullWidth
                                                startIcon={<DownloadIcon />}
                                                href={app.caminhoArquivo.replace('/home/wwgrup/tools/', 'https://gruporosinetos.com/tools/')}
                                                target="_blank"
                                            >
                                                Download
                                            </Button>
                                        </CardContent>
                                    </Card>
                                ))}
                                {apps.length === 0 && (
                                    <Typography sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
                                        Nenhum app disponível.
                                    </Typography>
                                )}
                            </Box>
                        ) : (
                            /* Versão Desktop em Tabela */
                            <MaterialReactTable
                                columns={columns}
                                data={apps}
                                localization={MRT_Localization_PT_BR}
                                enableColumnActions={false}
                                enableColumnFilters={false}
                                enablePagination={true}
                                enableSorting={true}
                                enableBottomToolbar={true}
                                enableTopToolbar={true}
                                muiTablePaperProps={{
                                    elevation: 0,
                                    sx: { borderRadius: 2, border: '1px solid', borderColor: 'divider' }
                                }}
                            />
                        )}
                    </Box>
                </Box>

                {/* Modal de Upload */}
                <Dialog open={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)} fullWidth maxWidth="sm">
                    <DialogTitle sx={{ fontWeight: 'bold' }}>Novo Upload de Ferramenta</DialogTitle>
                    <DialogContent>
                        <Box component="form" sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <FormControl fullWidth>
                                <InputLabel>Tipo</InputLabel>
                                <Select value={tipo} onChange={(e) => setTipo(e.target.value)} label="Tipo">
                                    <MenuItem value="APP">APP</MenuItem>
                                    <MenuItem value="SOFTWARE">Software</MenuItem>
                                    <MenuItem value="OUTRO">Outro</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl fullWidth>
                                <InputLabel>Área</InputLabel>
                                <Select value={area} onChange={(e) => setArea(e.target.value)} label="Área">
                                    <MenuItem value="Ronda Motorizada">Ronda Motorizada</MenuItem>
                                    <MenuItem value="Ronda Virtual">Ronda Virtual</MenuItem>
                                    <MenuItem value="Almoxarifado">Almoxarifado</MenuItem>
                                    <MenuItem value="Geral">Geral</MenuItem>
                                </Select>
                            </FormControl>

                            <TextField
                                label="Versão"
                                variant="outlined"
                                fullWidth
                                value={versao}
                                onChange={(e) => setVersao(e.target.value)}
                                placeholder="Ex: 1.0.5"
                            />

                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                    Arquivo (APK, ZIP, etc.)
                                </Typography>
                                <input
                                    type="file"
                                    onChange={(e) => setFile(e.target.files[0])}
                                    style={{ width: '100%' }}
                                />
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                                <Button onClick={() => setIsUploadModalOpen(false)}>Cancelar</Button>
                                <Button
                                    variant="contained"
                                    onClick={handleUpload}
                                    disabled={uploadLoading}
                                >
                                    {uploadLoading ? <CircularProgress size={24} /> : 'Salvar Ferramenta'}
                                </Button>
                            </Box>
                        </Box>
                    </DialogContent>
                </Dialog>
            </Box>
        </ThemeProvider>
    );
}
